import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, Input, input, output, inject, Optional, HostListener } from '@angular/core';
import { convertToDate, isDateFormat } from '../../utils/date.util';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { PhoneMaskPipe } from '../../pipes';
import { ViewOnlyType } from '../../../core';
import { QuoteProductMultiselectComponent } from '../../../components/quote/quote-summary/quote-product-multi-select';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
  selector: 'app-card-view-only',
  templateUrl: './card-view-only.component.html',
  styleUrls: ['./card-view-only.component.scss'],
  imports: [CommonModule, CardModule, ButtonModule, DividerModule, PhoneMaskPipe, QuoteProductMultiselectComponent],
})
export class CardViewOnlyComponent implements OnInit {
  private config = inject(DynamicDialogConfig, { optional: true });
  private dialogRef = inject(DynamicDialogRef, { optional: true });

  // Input signals for regular component usage (kept with original names for template bindings)
  dataList = input<ViewOnlyType[]>([]);
  id = input<string>('');
  hasPermissionEdit = input<boolean>(false);
  isQuote = input<boolean>(false);
  phoneName = input<string>('');
  title = input<string>();
  toggleEdit = output<void>();
  skip = input<string[]>([]);

  // Internal computed signals that check dialog config first, then inputs
  private _dataList = computed<ViewOnlyType[]>(() => {
    if (this.config?.data?.dataList) {
      return this.config.data.dataList;
    }
    return this.dataList();
  });

  private _id = computed<string>(() => {
    if (this.config?.data?.id) {
      return this.config.data.id;
    }
    return this.id();
  });

  private _hasPermissionEdit = computed<boolean>(() => {
    if (this.config?.data?.hasPermissionEdit !== undefined) {
      return this.config.data.hasPermissionEdit;
    }
    return this.hasPermissionEdit();
  });

  private _isQuote = computed<boolean>(() => {
    if (this.config?.data?.isQuote !== undefined) {
      return this.config.data.isQuote;
    }
    return this.isQuote();
  });

  private _phoneName = computed<string>(() => {
    if (this.config?.data?.phoneName) {
      return this.config.data.phoneName;
    }
    return this.phoneName();
  });

  private _title = computed<string | undefined>(() => {
    if (this.config?.data?.title) {
      return this.config.data.title;
    }
    return this.title();
  });

  // Getters for template to use computed values
  getDataList() {
    return this._dataList();
  }
  getId() {
    return this._id();
  }
  getHasPermissionEdit() {
    return this._hasPermissionEdit();
  }
  getIsQuote() {
    return this._isQuote();
  }
  getPhoneName() {
    return this._phoneName();
  }
  getTitle() {
    return this._title();
  }

  ngOnInit() {}

  closeDialog(): void {
    this.dialogRef?.close();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    event.preventDefault();
    this.closeDialog();
  }

  isGuid(value: any): boolean {
    if (!value) {
      return false;
    }
    const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    return guidRegex.test(value.toString());
  }

  filteredDataList = computed(() => {
    return this.getDataList()
      .filter((data) => !this.isGuid(data.value))
      .map((data: ViewOnlyType) => {
        if (data.subItems?.length) {
          const smoker = data.subItems.find((s) => s.name === 'Smoker');
          if (smoker) smoker.name = 'Tobacco User';
        }
        if (data.value && isDateFormat(data.value)) {
          data.value = convertToDate(data.value);
        }
        return data;
      });
  });

  shouldSkip(name: string) {
    return this.skip().some((x) => x === name);
  }

  getFieldIcon(fieldName: string): string {
    const iconMap: { [key: string]: string } = {
      Customer: 'pi-user',
      Phone: 'pi-phone',
      'Phone Number': 'pi-phone',
      Language: 'pi-language',
      Os: 'pi-desktop',
      'Operating System': 'pi-desktop',
      'User Agent': 'pi-info-circle',
      'Ip Address': 'pi-globe',
      'IP Address': 'pi-globe',
      'Consent At': 'pi-calendar',
      'Consent Type': 'pi-tag',
      'Conversation ID': 'pi-comments',
      'Conversation Id': 'pi-comments',
      Accept: 'pi-check-circle',
      'Created On': 'pi-clock',
      'Modified On': 'pi-pencil',
      'Created At': 'pi-clock',
      'Modified At': 'pi-pencil',
    };

    // Try exact match first
    if (iconMap[fieldName]) {
      return iconMap[fieldName];
    }

    // Try case-insensitive match
    const lowerFieldName = fieldName.toLowerCase();
    for (const key in iconMap) {
      if (key.toLowerCase() === lowerFieldName) {
        return iconMap[key];
      }
    }

    // Default icon
    return 'pi-info-circle';
  }

  getFieldIconColor(fieldName: string): string {
    const colorMap: { [key: string]: string } = {
      Customer: 'text-blue-600',
      Phone: 'text-blue-600',
      'Phone Number': 'text-blue-600',
      Language: 'text-purple-600',
      Os: 'text-orange-600',
      'Operating System': 'text-orange-600',
      'User Agent': 'text-gray-600',
      'Ip Address': 'text-green-600',
      'IP Address': 'text-green-600',
      'Consent At': 'text-indigo-600',
      'Consent Type': 'text-pink-600',
      'Conversation ID': 'text-cyan-600',
      'Conversation Id': 'text-cyan-600',
      Accept: 'text-emerald-600',
      'Created On': 'text-slate-600',
      'Modified On': 'text-amber-600',
      'Created At': 'text-slate-600',
      'Modified At': 'text-amber-600',
    };

    // Try exact match first
    if (colorMap[fieldName]) {
      return colorMap[fieldName];
    }

    // Try case-insensitive match
    const lowerFieldName = fieldName.toLowerCase();
    for (const key in colorMap) {
      if (key.toLowerCase() === lowerFieldName) {
        return colorMap[key];
      }
    }

    // Default color
    return 'text-gray-600';
  }
}
